/**
 * The receipt, read by a person before the customer reads it.
 *
 * Jonathon, 2026-09-12, on the receipt a check or cash payment gets: "this
 * email needs to be a prompt we can review before sending it." Everything
 * about the receipt itself is unchanged — the numbers, the PDF, the letterhead
 * and the filing on Docs are what they were (ADR 0125). What changed is that
 * nothing leaves until somebody has looked at it (ADR 0140).
 *
 * Two ways to arrive here. A rep who has just been handed a check sees it the
 * moment they record the payment. A receipt the software drew up by itself —
 * a check the bank cleared, matched to the one customer owed exactly that
 * amount — waits on the rep's list until somebody opens it.
 *
 * The wording is editable, because a receipt is a letter to a customer and the
 * person sending it knows things the template does not. What they approve is
 * what is sent, verbatim; the PDF goes with it either way.
 */

import { cents, formatMoney } from "@roof-hawk/core";
import { useEffect, useRef, useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import {
  draftCustomerPaymentReceipt,
  sendCustomerPaymentReceipt,
} from "../../lib/payments";
import { useSession } from "../../session";

export function ReceiptReviewModal({
  paymentId,
  onClose,
  onSent,
}: {
  paymentId: string;
  onClose: () => void;
  onSent?: () => Promise<void> | void;
}) {
  const { profileId } = useSession();
  const draft = useAsync(
    () => draftCustomerPaymentReceipt(paymentId, profileId),
    [paymentId, profileId],
  );

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filled in once the letter has been drawn. Not derived state: the whole
  // point is that it can then be edited.
  useEffect(() => {
    if (!draft.data) return;
    setSubject(draft.data.subject);
    setBody(draft.data.text);
  }, [draft.data]);

  const to = draft.data?.to ?? null;

  // The same guard the Save button needs, for the same reason: `busy` is state
  // and two clicks in one tick both read it as false, so the customer gets the
  // receipt twice. A ref is written and read in the same tick.
  const sending = useRef(false);

  const send = async () => {
    if (sending.current) return;
    sending.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await sendCustomerPaymentReceipt(paymentId, profileId, {
        wording: { subject, text: body },
        // The very PDF shown above, not one drawn again a moment later: a
        // payment recorded in between would otherwise change the attachment
        // after somebody had read it (Josh, 2026-09-13).
        document: draft.data?.document,
      });
      if (!result.sent) {
        setError(result.why ?? t("The receipt did not go."));
        return;
      }
      await onSent?.();
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      sending.current = false;
      setBusy(false);
    }
  };

  return (
    <Modal
      title={
        draft.data
          ? t("Receipt {number} — {amount}", {
              number: draft.data.receiptNumber,
              amount: formatMoney(cents(draft.data.amountCents)),
            })
          : t("Receipt")
      }
      subtitle={t(
        "Read it over. Nothing is sent unless you press Send, and the receipt PDF goes with it.",
      )}
      roomy
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            {t("Don’t send")}
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !draft.data || !to}
            onClick={() => void send()}
          >
            {busy ? t("Sending…") : t("Send the receipt")}
          </button>
        </>
      }
    >
      <ErrorNote error={error ?? draft.error ?? null} />
      {!draft.data ? (
        <p className="na-hint">{t("Drawing the receipt…")}</p>
      ) : (
        <>
          <p className="na-hint">
            {/* What the attached receipt says, so the figures are read rather
                than taken on trust. */}
            <b>{t(draft.data.settles ?? "Payment")}</b>
            {draft.data.balanceAfter
              ? ` · ${t("balance after this: {amount}", { amount: draft.data.balanceAfter })}`
              : ""}
          </p>
          <p className="na-hint">
            {to ? (
              <>
                {t("To")} <b>{to}</b>
                {draft.data.customerName ? ` · ${draft.data.customerName}` : ""}
              </>
            ) : (
              // The receipt still exists and is still filed on Docs. What is
              // missing is somewhere to send it, and saying so beats a button
              // that fails.
              <>
                {t(
                  "There is no email address on file for this customer, so this one has to be handed over or posted.",
                )}
              </>
            )}
          </p>
          <div className="field">
            <label htmlFor="receipt-subject">{t("Subject")}</label>
            <input
              id="receipt-subject"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="receipt-body">{t("Message")}</label>
            <textarea
              id="receipt-body"
              rows={12}
              value={body}
              onChange={(event) => setBody(event.target.value)}
            />
          </div>
        </>
      )}
    </Modal>
  );
}
